import React, { useEffect, useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import BottomNav from "./BottomNav";
import getCroppedImg from "./pages/utils/cropImage";
import "../src/ProfilePage.css";
import { notificationService } from './utils/notificationService';
import { saveAvatar } from "./utils/avatarService";
import { getAvatarUrl } from "./utils/imageUrls";
import CompleteProfileModal from "./CompleteProfileModal";
import { BIO_MAX } from "./utils/usernameService";
import TopMindsBadge from "./TopMindsBadge";

// Treats true / "yes" / "true" (any case) as on.
const isYes = (v) =>
  v === true || ["yes", "true"].includes(String(v ?? "").trim().toLowerCase());

const Profile = () => {
  const [user, setUser] = useState(null);
  const [uid, setUid] = useState(null);
  const [profileUrl, setProfileUrl] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Bio state
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioText, setBioText] = useState("");
  const [isSavingBio, setIsSavingBio] = useState(false);

  // Username (source of truth: users/{uid}.username in Firestore)
  const [username, setUsername] = useState("");
  const [usernameChangedAt, setUsernameChangedAt] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Proud Moments (set manually on the user doc in Firestore)
  const [showProud, setShowProud] = useState(false);
  const [hasTopMinds, setHasTopMinds] = useState(false);

  const navigate = useNavigate();
  const authInstance = getAuth();
  const db = getFirestore();

  const triggerImageUpload = () => {
    document.getElementById('profile-pic-upload').click();
  };

  const handleMenuClick = (path) => {
    navigate(path);
  };

  // One Firestore read gives us bio + username + last change time.
  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;

    const fetchProfileData = async () => {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : {};
        setBioText(data.bio || "");
        setUsername(data.username || "");
        setUsernameChangedAt(data.usernameChangedAt?.toMillis?.() ?? null);
        setShowProud(isYes(data.proudMoments));
        setHasTopMinds(isYes(data.topMindsAward));
      } catch (e) {
        console.error("Profile load error", e);
      }
    };
    fetchProfileData();

    return () => {
      cancelled = true;
    };
  }, [uid, db]);

  // The popup announces saves, so this page updates without another read.
  useEffect(() => {
    const onUpdated = (e) => {
      const d = e.detail || {};
      if (d.username !== undefined) setUsername(d.username);
      if (d.bio !== undefined) setBioText(d.bio);
      if (d.usernameChangedAt) setUsernameChangedAt(d.usernameChangedAt);
      if (d.profileUrl) setProfileUrl(d.profileUrl);
    };
    window.addEventListener("profile-updated", onUpdated);
    return () => window.removeEventListener("profile-updated", onUpdated);
  }, []);

  const saveBio = async () => {
    if (!uid || isSavingBio) return;
    setIsSavingBio(true);
    try {
      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, { bio: bioText }, { merge: true });
      setIsEditingBio(false);
      notificationService.showSuccess("Story saved successfully!");
    } catch (error) {
      console.error("Error saving bio:", error);
      notificationService.showError("Failed to save bio.");
    } finally {
      setIsSavingBio(false);
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(authInstance, (currentUser) => {
      setUser(currentUser);
      setUid(currentUser?.uid || null);
    });
    return unsub;
  }, [authInstance]);

  // Avatar: public URL built instantly from the stored path (no signing, no request)
  useEffect(() => {
    if (user?.photoURL) setProfileUrl(getAvatarUrl(user.photoURL));
  }, [user]);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result);
        setShowCropper(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const handleCropDone = async () => {
    if (uploading) return;
    setUploading(true);

    try {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      setShowCropper(false);

      // Resizes to a small square, uploads, updates Auth + Firestore
      const { url } = await saveAvatar({ uid, source: cropped });
      URL.revokeObjectURL(cropped);

      setProfileUrl(url);
      notificationService.showSuccess("Profile picture updated successfully!");
    } catch (err) {
      console.error("Crop or Upload failed:", err);
      notificationService.showError("Failed to update profile picture. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Must stay BEFORE anything that reads `user.` below
  if (!user) return <div className="profile-loading">Loading...</div>;

  const shownName = username ? `@${username}` : (user.displayName || "User");
  const avatarLetter = (username || user.displayName || user.email || '?').charAt(0).toUpperCase();

  return (
    <div className="profile-page">
      {showCropper ? (
        <div className="profile-cropper-container">
          <div className="profile-cropper-wrapper">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div className="profile-cropper-buttons">
            <button className="profile-cropper-save-btn" onClick={handleCropDone} disabled={uploading}>
              {uploading ? "Uploading..." : "Save"}
            </button>
            <button className="profile-cropper-cancel-btn" onClick={() => setShowCropper(false)} disabled={uploading}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {/* Edit Profile popup (same component as the first-time setup, but dismissible) */}
      {showEditModal && (
        <CompleteProfileModal
          uid={uid}
          mandatory={false}
          initialUsername={username}
          initialBio={bioText}
          usernameChangedAt={usernameChangedAt}
          avatarUrl={profileUrl}
          onClose={() => setShowEditModal(false)}
          onSaved={() => setShowEditModal(false)}
        />
      )}

      {/* Hidden Input for Profile Pic */}
      <input
        type="file"
        id="profile-pic-upload"
        accept="image/*"
        onChange={handleImageChange}
        hidden
      />

      {/* Header */}
      <div className="profile-header">
        <h1 className="profile-title">Profile</h1>
      </div>

      {/* Profile Section */}
      <div className="profile-section">
        <div className="profile-row">
          <div className="profile-avatar-wrap" onClick={triggerImageUpload}>
            {profileUrl ? (
              <img
                src={profileUrl}
                alt={username || user.displayName}
                className="profile-avatar-image"
                loading="lazy"
              />
            ) : (
              <div className="profile-avatar-fallback">
                {avatarLetter}
              </div>
            )}
            <span className="profile-avatar-edit-badge" aria-hidden="true">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
            </span>
          </div>
          <div className="profile-info">
            <h2 className="profile-user-name">{shownName}</h2>

            <div className="profile-user-email">{user.email}</div>

            {/* Action Buttons */}
            <div className="profile-action-row">
              <button className="profile-action-btn" onClick={() => setShowEditModal(true)}>
                Edit Profile
              </button>
              <button className="profile-action-btn profile-share-btn" onClick={() => {
                const url = window.location.origin + "/journey/" + user.uid;
                if (navigator.share) {
                  navigator.share({
                    title: `Check out my profile on Trescend`,
                    url: url
                  });
                } else {
                  navigator.clipboard.writeText(url);
                  alert('Profile link copied to clipboard!');
                }
              }}>
                Share Profile
              </button>
            </div>
          </div>
        </div>


      {/* Proud Moments — only when users/{uid}.proudMoments is "yes" */}
      {showProud && (
        <div className="profile-menu-list profile-proud-section">
          <div className="profile-proud-inner">
            <h3 className="profile-bio-title profile-proud-title">Proud Moments</h3>
            {hasTopMinds ? (
              <TopMindsBadge />
            ) : (
              <p className="profile-proud-empty">Your proud moments will appear here.</p>
            )}
          </div>
        </div>
      )}


        {/* My Story (Bio) Section */}
        <div className="profile-bio-section">
          <div className="profile-bio-header-row">
            <h3 className="profile-bio-title">My Story</h3>
            {!isEditingBio && (
              <button
                className="profile-bio-edit-btn"
                onClick={() => setIsEditingBio(true)}
              >
                {bioText ? 'Edit' : 'Add'}
              </button>
            )}
            {isEditingBio && (
              <button
                className="profile-bio-edit-btn"
                onClick={saveBio}
                disabled={isSavingBio}
              >
                {isSavingBio ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>
          {isEditingBio ? (
            <textarea
              className="profile-bio-input"
              value={bioText}
              onChange={(e) => {
                if (e.target.value.length <= BIO_MAX) setBioText(e.target.value);
              }}
              placeholder="Tell your story..."
              rows={4}
            />
          ) : (
            <p className="profile-bio-text">
              {bioText || "Share your journey with the world..."}
            </p>
          )}
        </div>
      </div>

      {/* Menu List — Wallet, Saved, Settings all in one card */}
      <div className="profile-menu-list">


        <div className="profile-menu-item" onClick={() => handleMenuClick('/saved')}>
          <div className="profile-menu-left">
            <img src={require('./assets/saved.png')} alt="Saved" className="profile-menu-icon" />
            <span>Saved</span>
          </div>
          <span className="profile-menu-arrow">{'>'}</span>
        </div>

        <div className="profile-menu-item" onClick={() => handleMenuClick('/settings')}>
          <div className="profile-menu-left">
            <img src={require('./assets/settings.png')} alt="Settings" className="profile-menu-icon" />
            <span>Settings</span>
          </div>
          <span className="profile-menu-arrow">{'>'}</span>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Profile;